import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../components/Layouts/Main/MainLayout.index";
import { getAnalyticsAccounts, getAnalyticsInfo } from "../../api/project";
import "./Analytics.css";

const safeNumber = (value) =>
  value === null ||
  value === undefined ||
  value === "" ||
  !Number.isFinite(Number(value))
    ? null
    : Number(value);
const fmt = (value, digits = 0) => {
  const n = safeNumber(value);
  return n === null
    ? "—"
    : n.toLocaleString("ja-JP", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
};
const percent = (part, whole) => {
  const p = safeNumber(part);
  const w = safeNumber(whole);
  return p !== null && w !== null && w > 0 ? (p / w) * 100 : null;
};

// 数値が変わったときだけアニメーション。データ未取得時は「—」を維持。
function AnimatedNumber({ value, digits = 0, unit = "" }) {
  const number = safeNumber(value);
  const [display, setDisplay] = useState(number);
  const prevRef = useRef(number);

  useEffect(() => {
    if (number === null) {
      prevRef.current = null;
      setDisplay(null);
      return undefined;
    }
    const from = prevRef.current === null ? 0 : prevRef.current;
    const to = number;
    prevRef.current = to;
    if (
      typeof window === "undefined" ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
    ) {
      setDisplay(to);
      return undefined;
    }
    let frame;
    const start = performance.now();
    const duration = 650;
    const tick = (time) => {
      const progress = Math.min(1, (time - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(from + (to - from) * eased);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [number]);

  return (
    <>
      {fmt(display, digits)}
      {number !== null && (
        <span className="analytics-metric__unit">{unit}</span>
      )}
    </>
  );
}

function Help({ children, label = "指標の説明" }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="analytics-help">
      <button
        type="button"
        className="analytics-help__button"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        ⓘ
      </button>
      {open && (
        <span className="analytics-help__content" role="note">
          {children}
        </span>
      )}
    </span>
  );
}

function Metric({
  title,
  value,
  digits = 0,
  unit = "",
  description,
  explanation,
}) {
  return (
    <article className="analytics-metric">
      <div className="analytics-metric__title">
        {title} <Help label={`${title}の説明`}>{explanation}</Help>
      </div>
      <div className="analytics-metric__value">
        <AnimatedNumber value={value} digits={digits} unit={unit} />
      </div>
      <div className="analytics-metric__description">{description}</div>
    </article>
  );
}

function getInsights(data, accountId, activeDays, related) {
  const total = data?.totalUsers || {};
  const active = data?.activeUsers || {};
  const results = [];
  const totalCount = safeNumber(total.userCount);
  const activeCount = safeNumber(active.userCount);
  const totalAvg = safeNumber(total.averageFavorites);
  const activeAvg = safeNumber(active.averageFavorites);
  const totalShare = safeNumber(total.marketSharePercent);
  const activeShare = safeNumber(active.marketSharePercent);

  if (totalCount !== null && activeCount !== null && totalCount > 0) {
    const rate = percent(activeCount, totalCount);
    results.push(
      `直近${activeDays}日間に利用履歴が確認された登録ユーザーは${fmt(activeCount)}人で、登録ユーザー${fmt(totalCount)}人の${fmt(rate, 1)}%です。`,
    );
  } else {
    results.push(
      "全ユーザー数とアクティブユーザー数が揃っていないため、アクティブ率は判定できません。",
    );
  }

  if (totalAvg !== null && activeAvg !== null) {
    const difference = activeAvg - totalAvg;
    const comparison =
      Math.abs(difference) < 0.05
        ? "ほぼ同水準です"
        : `全ユーザーより${fmt(Math.abs(difference), 1)}件${difference > 0 ? "多い" : "少ない"}状況です`;
    results.push(
      `お気に入り平均は全ユーザー${fmt(totalAvg, 1)}件、アクティブユーザー${fmt(activeAvg, 1)}件で、アクティブユーザーは${comparison}。`,
    );
  } else {
    results.push("お気に入り平均の比較に必要なデータが揃っていません。");
  }

  if (!accountId) {
    results.push(
      "分析対象のアカウントを選択すると、市場占有率と関連アカウントを分析できます。",
    );
  } else {
    if (totalShare !== null && activeShare !== null) {
      const gap = activeShare - totalShare;
      const relation =
        Math.abs(gap) < 0.05 ? "ほぼ同水準" : gap > 0 ? "高い" : "低い";
      results.push(
        `選択アカウントのお気に入り率は全ユーザー${fmt(totalShare, 1)}%、アクティブユーザー${fmt(activeShare, 1)}%で、アクティブ層の方が${relation}状況です。`,
      );
    } else {
      results.push("市場占有率の比較に必要なデータが揃っていません。");
    }
    if (related.length > 0) {
      const top = related[0];
      results.push(
        `関連アカウント1位は「${top.accountName || top.accountId || "名称未設定"}」で、共通ファンは${fmt(top.sharedFanCount)}人です。`,
      );
    } else {
      results.push("関連アカウントの集計結果はありません。");
    }
  }
  return results;
}

export default function Analytics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeDays, setActiveDays] = useState(30);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [accountsError, setAccountsError] = useState("");

  useEffect(() => {
    let mounted = true;
    getAnalyticsAccounts()
      .then((res) => {
        if (!mounted) return;
        if (res?.code !== 200)
          throw new Error("アカウント一覧の取得に失敗しました");
        setAccounts(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        if (mounted)
          setAccountsError(
            err?.message || "アカウント一覧の取得に失敗しました",
          );
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    getAnalyticsInfo({ activeDays, accountId })
      .then((res) => {
        if (!mounted) return;
        if (res?.code !== 200) throw new Error("データの取得に失敗しました");
        setData(res.data || null);
      })
      .catch((err) => {
        if (mounted) {
          setError(err?.message || "データの取得に失敗しました");
          setData(null);
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [activeDays, accountId]);

  const total = data?.totalUsers || {};
  const active = data?.activeUsers || {};
  const related = useMemo(
    () =>
      Array.isArray(data?.relatedAccounts)
        ? data.relatedAccounts.slice(0, 20)
        : [],
    [data],
  );
  const maxRelated = Math.max(
    1,
    ...related.map((item) => Number(item.sharedFanCount) || 0),
  );
  const insights = useMemo(
    () => (data ? getInsights(data, accountId, activeDays, related) : []),
    [data, accountId, activeDays, related],
  );
  const subjectFanCount = safeNumber(data?.subjectFanCount);

  return (
    <MainLayout>
      <main className="analytics-page">
        <header className="analytics-heading">
          <div>
            <h1>アナリティクス</h1>
            <p>雀活マスターのお気に入り傾向と関連アカウントを分析します。</p>
          </div>
        </header>

        <div className="analytics-controls">
          <label className="analytics-select">
            分析対象
            <select
              value={accountId}
              onChange={(event) => setAccountId(event.target.value)}
            >
              <option value="">アカウントを選択してください</option>
              {accounts.map((account) => (
                <option key={account.accountId} value={account.accountId}>
                  {account.accountName}（{account.accountId}）
                </option>
              ))}
            </select>
          </label>
          <label className="analytics-select">
            アクティブ判定期間
            <select
              value={activeDays}
              onChange={(event) => setActiveDays(Number(event.target.value))}
            >
              <option value={7}>過去7日</option>
              <option value={30}>過去30日</option>
              <option value={90}>過去90日</option>
            </select>
          </label>
        </div>
        <p className="analytics-note">
          期間には本日を含みます。全ユーザーは登録ユーザー、アクティブユーザーは利用ログと登録情報が照合できたユーザーです。
        </p>
        {accountsError && (
          <p className="analytics-error" role="alert">
            {accountsError}
          </p>
        )}
        {loading && (
          <p role="status" className="analytics-status">
            読み込み中...
          </p>
        )}
        {error && (
          <p className="analytics-error" role="alert">
            {error}
          </p>
        )}

        {!loading && !error && data && (
          <>
            <section
              className="analytics-section analytics-summary"
              aria-label="総合分析結果"
            >
              <div className="analytics-section__header">
                <h2>総合分析結果</h2>
                <span className="analytics-tag">データから自動生成</span>
              </div>
              <ul className="analytics-insights">
                {insights.map((insight, i) => (
                  <li key={i}>{insight}</li>
                ))}
              </ul>
              <p className="analytics-note">
                ※
                数値の比較結果を表示しています。増減の原因や因果関係を示すものではありません。
              </p>
            </section>

            <section className="analytics-section">
              <div className="analytics-section__header">
                <h2>全ユーザー</h2>
                <span className="analytics-note">登録ユーザー全体</span>
              </div>
              <div className="analytics-cards">
                <Metric
                  title="お気に入り平均"
                  value={total.averageFavorites}
                  digits={1}
                  unit="件"
                  description="登録ユーザー1人あたりの平均"
                  explanation="全登録ユーザーのお気に入り総件数 ÷ 登録ユーザー数。お気に入り0件のユーザーも含みます。"
                />
                <Metric
                  title="市場占有率"
                  value={accountId ? total.marketSharePercent : null}
                  digits={1}
                  unit="%"
                  description="対象をお気に入りしている割合"
                  explanation="選択アカウントをお気に入りしている登録ユーザー数 ÷ 全登録ユーザー数 × 100。ダウンロード数を分母とする指標ではありません。"
                />
                <Metric
                  title="ユーザー数"
                  value={total.userCount}
                  unit="人"
                  description="登録ユーザーの母数"
                  explanation="j_fansに登録されているユーザーを基準とした人数。累計ダウンロード数とは異なります。"
                />
              </div>
            </section>

            <section className="analytics-section">
              <div className="analytics-section__header">
                <h2>アクティブユーザー</h2>
                <span className="analytics-note">直近{activeDays}日間</span>
              </div>
              <div className="analytics-cards">
                <Metric
                  title="お気に入り平均"
                  value={active.averageFavorites}
                  digits={1}
                  unit="件"
                  description="活動が確認されたユーザーの平均"
                  explanation={`直近${activeDays}日間に利用ログを確認できた登録ユーザーのお気に入り総件数 ÷ アクティブユーザー数。0件のユーザーも含みます。`}
                />
                <Metric
                  title="アクティブ市場占有率"
                  value={accountId ? active.marketSharePercent : null}
                  digits={1}
                  unit="%"
                  description="アクティブ層のうち対象をお気に入りした割合"
                  explanation="対象をお気に入りしているアクティブユーザー数 ÷ アクティブユーザー数 × 100。"
                />
                <Metric
                  title="アクティブユーザー数"
                  value={active.userCount}
                  unit="人"
                  description={`過去${activeDays}日のユニークユーザー数`}
                  explanation="過去分はj_ad_display_user_daily、当日分はj_ad_display_logから取得したニックネームを、j_fans.user_nameと照合した登録ユーザー数。同一ユーザーは1人に集計します。ログの記録漏れやニックネーム変更で実際の利用者と差が出る可能性があります。"
                />
              </div>
            </section>

            <section className="analytics-section">
              <div className="analytics-section__header">
                <h2>
                  関連アカウント TOP20{" "}
                  <Help label="関連アカウントの説明">
                    選択アカウントをお気に入りしているファンが、他にお気に入りしているアカウントを共通ファン数順に表示します。対象自身は除外します。
                  </Help>
                </h2>
              </div>
              <p className="analytics-note">
                人数＝両方をお気に入りしているファン数。割合＝共通ファン数 ÷
                対象アカウントのファン数。複数アカウントをお気に入りできるため割合の合計は100%になりません。
              </p>
              {!accountId ? (
                <p className="analytics-empty">
                  分析対象のアカウントを選択してください。
                </p>
              ) : related.length === 0 ? (
                <p className="analytics-empty">
                  関連アカウントのデータがありません。
                </p>
              ) : (
                <div className="analytics-ranking">
                  {related.map((item, index) => {
                    const count = safeNumber(item.sharedFanCount) || 0;
                    return (
                      <div
                        className="analytics-ranking__item"
                        key={item.accountId || index}
                      >
                        <span className="analytics-ranking__position">
                          {index + 1}
                        </span>
                        <span
                          className="analytics-ranking__name"
                          title={item.accountName}
                        >
                          {item.accountName || item.accountId || "名称未設定"}
                        </span>
                        <div
                          className="analytics-ranking__bar-background"
                          aria-hidden="true"
                        >
                          <div
                            className="analytics-ranking__bar"
                            style={{
                              width: `${Math.min(100, Math.max(0, (count / maxRelated) * 100))}%`,
                            }}
                          />
                        </div>
                        <strong className="analytics-ranking__count">
                          <AnimatedNumber value={count} unit="人" />
                        </strong>
                        <span className="analytics-ranking__ratio">
                          {percent(count, subjectFanCount) === null
                            ? "—"
                            : `${fmt(percent(count, subjectFanCount), 1)}%`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </MainLayout>
  );
}
